import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Modal } from 'antd'
import { DefaultJsonForm } from '../../../../../../components/v2/default-json-form'
import { convertData2JsonForm } from '../../../../../../shared/lib/convertData2JsonForm'
import fetchAPI from '../../../../../../../lib/utils/fetch-api'
import { isFormDataRequired } from '../../../../../../../lib/utils/collections'
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch/useAppDispatch'
import { getClaimWorkData } from '../../../../../../store/claim_work/action-creators'
import { getTimeLineData } from '../../../../../../store/timeline/action-creators'

interface IFormData {
  date_work_completion?: string
  time_work_completion?: string | string[]
  comment?: string
}

interface ClaimWorkEditDateModalProps {
  id: string | number
  isModalOpen: boolean
  onCancel?: (e: React.MouseEvent<HTMLElement>) => void
  onOk?: () => void
}

const TIME_OPTIONS = [
  { const: '09:00 - 11:00', title: '09:00 - 11:00' },
  { const: '11:00 - 13:00', title: '11:00 - 13:00' },
  { const: '13:00 - 15:00', title: '13:00 - 15:00' },
  { const: '15:00 - 17:00', title: '15:00 - 17:00' },
  { const: '17:00 - 19:00', title: '17:00 - 19:00' },
]

export const ClaimWorkEditDateModal: React.FC<ClaimWorkEditDateModalProps> = memo(
  (props: ClaimWorkEditDateModalProps) => {
    const { id, isModalOpen, onCancel, onOk } = props
    const dispatch = useAppDispatch()
    const [formData, setFormData] = useState<IFormData>({
      date_work_completion: undefined,
      time_work_completion: undefined,
      comment: undefined,
    })

    const handleOk = useCallback(async () => {
      const isRequiredFields = isFormDataRequired(formData, actualSchema)
      if (!isRequiredFields) {
        Modal.warning({
          title: 'Предупреждение',
          content: 'Заполните все обязательные поля',
        })
        return
      }

      const timeWorkCompletion = Array.isArray(formData.time_work_completion)
        ? formData.time_work_completion
        : formData.time_work_completion
          ? [formData.time_work_completion]
          : []

      try {
        const response = await fetchAPI(`/api/claim-work/${id}`, {
          method: 'POST',
          body: JSON.stringify({
            date_work_completion: formData.date_work_completion,
            time_work_completion: timeWorkCompletion,
            comment: formData.comment,
          }),
        })

        if (response?.errors) {
          const error = Array.isArray(response.errors) ? response.errors.join(', ') : response.errors
          Modal.error({
            title: 'Ошибка',
            content: `При сохранении даты возникла ошибка: ${error}.`,
          })
          return
        }
        onOk?.()
        await dispatch(getClaimWorkData(id))
        dispatch(getTimeLineData('claim_work', id))
        handleCancel({} as React.MouseEvent<HTMLElement>)
      } catch {
        Modal.error({
          title: 'Ошибка',
          content: 'При сохранении плановой даты завершения возникла ошибка.',
        })
      }
    }, [formData, id, onOk, dispatch])

    const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
      setFormData({
        date_work_completion: undefined,
        time_work_completion: undefined,
        comment: undefined,
      })
      onCancel?.(e)
    }

    const onChange = useCallback(data => {
      setFormData(prev => ({ ...prev, ...data.formData }))
    }, [])

    useEffect(() => {
      if (!isModalOpen) {
        setFormData({
          date_work_completion: undefined,
          time_work_completion: undefined,
          comment: undefined,
        })
      }
    }, [isModalOpen])

    interface ISchemaActual {
      resultSchema: {
        properties: {
          time_work_completion: { oneOf: unknown[]; enum: string[] }
        }
      }
    }

    const actualSchema = useMemo(() => {
      const schema = require('./schema.json')
      const schemaActual = convertData2JsonForm(schema.fields) as never as ISchemaActual
      if (schemaActual?.resultSchema?.properties?.time_work_completion) {
        schemaActual.resultSchema.properties.time_work_completion.oneOf = TIME_OPTIONS
        schemaActual.resultSchema.properties.time_work_completion.enum = TIME_OPTIONS.map(
          o => o.const,
        )
      }
      return schemaActual?.resultSchema || {}
    }, [])

    if (!isModalOpen) {
      return null
    }

    return (
      <Modal
        title="Изменить плановую дату завершения"
        onOk={handleOk}
        onCancel={handleCancel}
        open={isModalOpen}
        destroyOnHidden={true}
      >
        <DefaultJsonForm
          formData={formData}
          schema={actualSchema}
          onChange={onChange}
          orientation="horizontal"
        />
      </Modal>
    )
  },
)

ClaimWorkEditDateModal.displayName = 'ClaimWorkEditDateModal'
